import { useEffect, useRef, useState } from 'react';
import {
  FEEDBACK_KINDS,
  FEEDBACK_STATUSES,
  listFeedback,
  updateFeedback,
  type FeedbackReport,
  type FeedbackStatus,
} from '@/data/feedback';
import './feedback.css';

export function FeedbackCard({
  emailOf,
  reload = 0,
}: {
  emailOf: (id: string) => string;
  reload?: number;
}) {
  const [reports, setReports] = useState<FeedbackReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const listVersion = useRef(0);
  useEffect(() => {
    let current = true;
    const version = ++listVersion.current;
    const isCurrent = () => current && version === listVersion.current;
    setLoading(true);
    setError(null);
    void listFeedback()
      .then(
        (rows) => {
          if (isCurrent()) setReports(rows);
        },
        (e: unknown) => {
          if (isCurrent()) setError(e instanceof Error ? e.message : 'Could not load feedback.');
        },
      )
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [reload]);
  const visible = reports.filter(
    (r) => showClosed || r.status === 'open' || r.status === 'in_progress',
  );
  return (
    <section className="admin-card" data-testid="admin-feedback">
      <h2>Feedback</h2>
      <p className="muted">Replies and status changes appear in the reporter’s Feedback page.</p>
      <label>
        <input
          type="checkbox"
          checked={showClosed}
          onChange={(e) => setShowClosed(e.target.checked)}
        />{' '}
        Include closed reports
      </label>
      {loading && <p className="muted">Loading feedback…</p>}
      {error && (
        <p role="alert" className="home-load-error">
          Feedback: {error}
        </p>
      )}
      {!loading && !error && visible.length === 0 && (
        <p className="muted">No {showClosed ? '' : 'open '}reports.</p>
      )}
      {visible.map((report) => (
        <ReportReview
          key={report.id}
          report={report}
          reporter={emailOf(report.reporter)}
          onSaved={(saved) => {
            listVersion.current += 1;
            setLoading(false);
            setError(null);
            setReports((rows) => rows.map((r) => (r.id === saved.id ? saved : r)));
          }}
        />
      ))}
      {reports.length >= 200 && <p className="muted">Showing the latest 200 reports.</p>}
    </section>
  );
}

function ReportReview({
  report,
  reporter,
  onSaved,
}: {
  report: FeedbackReport;
  reporter: string;
  onSaved: (report: FeedbackReport) => void;
}) {
  const [status, setStatus] = useState(report.status);
  const [reply, setReply] = useState(report.reply);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // A refresh may bring another admin's update into this mounted card.
  useEffect(() => {
    setStatus(report.status);
    setReply(report.reply);
  }, [report.status, report.reply]);
  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      onSaved(await updateFeedback(report.id, status, reply));
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The change was refused.');
    } finally {
      setBusy(false);
    }
  };
  const tune = report.context.tune;
  return (
    <article className="feedback-report">
      <h3>
        {FEEDBACK_KINDS[report.kind]}
        {tune ? ` · ${tune.title}${tune.bar ? ` · bar ${tune.bar}` : ''}` : ''}
      </h3>
      <p className="muted">
        {reporter} · {new Date(report.created_at).toLocaleString()}
      </p>
      <p className="feedback-message">{report.message}</p>
      <details>
        <summary>Captured context</summary>
        <pre className="feedback-context">{JSON.stringify(report.context, null, 2)}</pre>
      </details>
      <form
        className="feedback-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label>
          Status
          <select
            aria-label="Status"
            value={status}
            disabled={busy}
            onChange={(e) => {
              setStatus(e.target.value as FeedbackStatus);
              setSaved(false);
            }}
          >
            {Object.entries(FEEDBACK_STATUSES).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Reply to reporter
          <textarea
            className="text-input"
            maxLength={2000}
            rows={3}
            disabled={busy}
            value={reply}
            onChange={(e) => {
              setReply(e.target.value);
              setSaved(false);
            }}
          />
        </label>
        <button className="btn btn-sm btn-primary" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save reply and status'}
        </button>
        {error && (
          <p role="alert" className="home-load-error">
            {error}
          </p>
        )}
        {saved && <p role="status">Saved. The reporter can see this update.</p>}
      </form>
    </article>
  );
}
